<template>
  <PageContainer>
    <View gap="lg" direction="column">
      <View justify="space-between" class="header" align="center">
        <Text variant="h1">{{ title }}</Text>

        <MenuButton :buttonLabel="selectedTimeFilter" size="medium">
          <MenuButtonItem
            v-for="(filter, index) in timeFilters"
            :key="index"
            :title="filter"
            @click="onFilterSelected(filter)"
          />
        </MenuButton>
      </View>

      <TopBarFilters :selected-filter="selectedFilter" :filters="filters" :counts="counts" @selected="filterLeads" />

      <View v-if="state.status === 'pending'" justify="center" padding="xl">
        <LoadingIndicator />
      </View>

      <EmptyPagePlaceholder
        v-else-if="state.status === 'error'"
        title="Could not load data"
        description="Please reload the page or try again later"
      />

      <template v-else>
        <EmptyPagePlaceholder
          v-if="filteredInquiries.length === 0"
          class="empty-state"
          :title="emptyResultTitle"
          :description="emptyResultDescription"
        />

        <div v-else class="renters">
          <RenterProfileLink
            v-for="propertyInquiry in filteredInquiries"
            :decoration="false"
            :key="propertyInquiry.renter.id"
            :property-id="propertyId"
            :renter-id="propertyInquiry.renter.id"
          >
            <LeadCard :property-inquiry="propertyInquiry" :mark-wants-to-apply="mixesWantsToApply" />
          </RenterProfileLink>
        </div>
      </template>
    </View>
  </PageContainer>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import {
  TopBarFilters,
  EmptyPagePlaceholder,
  PageContainer,
  Text,
  View,
  MenuButtonItem,
  MenuButton,
  LoadingIndicator,
} from '@tallo/design-system';
import { PropertyInquiryDto, usePropertyInquiriesQuery } from '@tallo/investor/property';
import { RenterProfileLink } from '@tallo/investor/renter';
import { TimeFilter, timeFilters } from '@tallo/utility';

import { InquiryFilter, InquiryOrder } from '../../consts/inquiry-filter.const';
import LeadCard from './lead-card.vue';

const props = defineProps<{
  title: string;
  emptyResultTitle: string;
  emptyResultDescription: string;
  filters: string[];
  /** A filter with no entry shows every inquiry */
  filtersMap: Map<string, InquiryFilter>;
  /** A filter with no entry keeps the order the inquiries came in */
  filtersOrder?: Map<string, InquiryOrder>;
  /** Filters whose chip shows how many inquiries match, out of the loaded time range */
  countedFilters?: string[];
  /** A counted filter whose count reads differently from its list; one with no entry counts its list */
  countsMap?: Map<string, InquiryFilter>;
}>();

const router = useRouter();
const route = useRoute();
const { state, inquiries, selectedTimeFilter } = usePropertyInquiriesQuery();

const propertyId = computed(() => route.params.propertyId as string);
const selectedFilter = computed(() => route.query.filter as string);
const filteredInquiries = computed(() => {
  const inquiriesUnder = inquiriesUnderFilter(selectedFilter.value);
  const order = props.filtersOrder?.get(selectedFilter.value);

  return order ? [...inquiriesUnder].sort(order) : inquiriesUnder;
});
const counts = computed(
  () =>
    new Map(
      (props.countedFilters ?? []).map((filter) => {
        const counted = props.countsMap?.get(filter) ?? props.filtersMap.get(filter);

        return [filter, counted ? inquiries.value.filter(counted).length : inquiries.value.length];
      }),
    ),
);
// Only then does the dot pick anyone out: under Wants to apply, or for an owner whose renters all ask to apply because
// they take applications elsewhere, every card would carry it. An ask the owner ignored carries no dot.
const waitsOnOwner = (inquiry: PropertyInquiryDto) =>
  !!inquiry.applicationRequestedAt && !inquiry.applicationRequestIgnored;
const mixesWantsToApply = computed(
  () => filteredInquiries.value.some(waitsOnOwner) && filteredInquiries.value.some((inquiry) => !waitsOnOwner(inquiry)),
);

function inquiriesUnderFilter(filter: string) {
  const matches = props.filtersMap.get(filter);

  return matches ? inquiries.value.filter(matches) : inquiries.value;
}

function filterLeads(filter: string) {
  router.replace({ query: { ...route.query, filter } });
}

function onFilterSelected(filter: TimeFilter) {
  selectedTimeFilter.value = filter;
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.header {
  width: 100%;
}

.renters {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(22rem, 1fr));
  gap: var(--t-spacing-md);

  @include screen-max-sm {
    grid-template-columns: 1fr;
  }
}
</style>
